import { displayName } from '../../../lib/dependents/helpers';

import type { IDependentResidences } from '../../../hooks/queries/useDependentResidences';
import type { IDependent, IDependentPartnership } from '../../../hooks/queries/useDependents';

export interface IDependentRow {
  dependent: IDependent;
  /**
   * True for the second card of a partnered pair. That card renders the
   * "Lives with {partnerName}" checkbox and is the one that can be Referential
   * (the Main/Referential decision itself is derived live in the form from the
   * editable `liveTogether` value — not frozen here).
   */
  isPartnerCard: boolean;
  /** The other dependent's display name — used for the partner-checkbox label and the referential copy. `null` for non-partner cards. */
  partnerName: string | null;
}

/** A dependent "owns" a residence when it's their own row, not a caregiver's or partner's resolved via cohabitation. */
const ownsPrimary = (dependentId: string, residences: IDependentResidences | undefined): boolean =>
  residences?.primary != null &&
  residences.primary.ownerKind === 'dependent' &&
  residences.primary.ownerId === dependentId;

/**
 * Order the dependent cards and mark which one is the "partner card", per the
 * settings spec:
 *
 *   - **Partnered pair living together** (`liveTogether === true`): the dependent
 *     who owns the primary residence is shown first (Main); the other is the
 *     partner card (Referential by default). When neither owns a primary, fall
 *     back to alphabetical and the first card is Main.
 *   - **Partnered pair living apart**: alphabetical; the second card is still the
 *     partner card (its "Lives with {first}" checkbox is unchecked, so it renders
 *     its own Main residence).
 *   - **Unpartnered or single**: alphabetical, no partner card.
 *
 * Card order is intentionally derived from the *server* `partnership` value so it
 * stays stable while the user toggles "Lives with {first}" — only the second
 * card's Main/Referential mode flips, the cards don't re-sort underfoot.
 */
export const orderDependents = (
  dependents: ReadonlyArray<IDependent>,
  residencesByDependentId: ReadonlyMap<string, IDependentResidences>,
  partnership: IDependentPartnership | null
): IDependentRow[] => {
  const byAlpha = (a: IDependent, b: IDependent): number => displayName(a).localeCompare(displayName(b));
  const isPartneredPair = dependents.length === 2 && partnership !== null;

  if (isPartneredPair) {
    const [a, b] = dependents;
    const liveTogether = partnership.liveTogether === true;
    const aOwns = ownsPrimary(a.id, residencesByDependentId.get(a.id));
    const bOwns = ownsPrimary(b.id, residencesByDependentId.get(b.id));

    let ordered: IDependent[];
    if (liveTogether && aOwns && !bOwns) {
      ordered = [a, b];
    } else if (liveTogether && bOwns && !aOwns) {
      ordered = [b, a];
    } else {
      ordered = [a, b].slice().sort(byAlpha);
    }

    const [first, second] = ordered;
    return [
      { dependent: first, isPartnerCard: false, partnerName: null },
      { dependent: second, isPartnerCard: true, partnerName: displayName(first) },
    ];
  }

  return dependents
    .slice()
    .sort(byAlpha)
    .map((d) => ({ dependent: d, isPartnerCard: false, partnerName: null }));
};

/**
 * Pin `rows` to a previously captured id order, so auto-saves that change the
 * ordering inputs (a rename, an address save, a partnership change) never
 * shuffle the rendered cards mid-session — the natural `orderDependents` sort
 * only applies again on a fresh mount. Rows added since the capture append at
 * the end in their natural order; captured ids with no matching row drop out.
 * A `null` order (nothing captured yet) returns the natural order.
 */
export const applyFrozenOrder = (rows: readonly IDependentRow[], order: readonly string[] | null): IDependentRow[] => {
  if (order === null) {
    return [...rows];
  }
  const byId = new Map(rows.map((row) => [row.dependent.id, row]));
  const pinned: IDependentRow[] = [];
  for (const id of order) {
    const row = byId.get(id);
    if (row) {
      pinned.push(row);
      byId.delete(id);
    }
  }
  return [...pinned, ...byId.values()];
};
